import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Lock, Unlock, AlertCircle } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useMutation } from "@tanstack/react-query";
import axios from "axios";

interface TrackingAuthFormProps {
  trackingNumber: string;
  onAuthSuccess: (token: string) => void;
}

// Define the expected API response type
interface AuthResponse {
  isAuthorized: boolean;
  message: string;
  token?: string;
  expiresIn?: string;
  error?: string;
}

export const TrackingAuthForm: React.FC<TrackingAuthFormProps> = ({
  trackingNumber,
  onAuthSuccess,
}) => {
  const [phoneLastFour, setPhoneLastFour] = useState("");

  // Use React Query's useMutation hook for the authentication API call
  const authMutation = useMutation({
    mutationFn: async (phoneLastFour: string) => {
      const response = await axios.post<AuthResponse>(
        `${import.meta.env.VITE_API_URL}/api/v1/parcels/${trackingNumber}/validate-sensitive-access`,
        { phoneLastFour }
      );
      return response.data;
    },
    onSuccess: (data) => {
      if (data.isAuthorized && data.token) {
        // Store token in localStorage for future requests
        localStorage.setItem(`tracking_token_${trackingNumber}`, data.token);

        // Store token expiration time if provided, or default to 24 hours
        const expiresInMs = data.expiresIn
          ? parseInt(data.expiresIn) * 1000
          : 24 * 60 * 60 * 1000; // Default to 24 hours
        const expirationTime = Date.now() + expiresInMs;
        localStorage.setItem(
          `tracking_token_expiry_${trackingNumber}`,
          expirationTime.toString()
        );

        // Call the success callback with the token
        onAuthSuccess(data.token);
      } else {
        throw new Error(data.message || "Authentication failed");
      }
    },
    onError: (error) => {
      console.error("Authentication error:", error);
      // Error handling is already done in the component via authMutation.error
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    authMutation.mutate(phoneLastFour);
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Lock className="h-5 w-5" />
          Authentication Required
        </CardTitle>
        <CardDescription>
          To view sensitive tracking information like delivery photos, please
          verify your identity.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {authMutation.isError && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>
              {authMutation.error instanceof Error
                ? authMutation.error.message
                : "An unexpected error occurred"}
            </AlertDescription>
          </Alert>
        )}
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label
                htmlFor="phoneLastFour"
                className="block text-sm font-medium mb-1"
              >
                Last 4 digits of sender or recipient phone number
              </label>
              <Input
                id="phoneLastFour"
                type="text"
                inputMode="numeric"
                pattern="[0-9]{4}"
                maxLength={4}
                placeholder="1234"
                value={phoneLastFour}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setPhoneLastFour(e.target.value)
                }
                required
                className="w-full"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Enter the last 4 digits of the phone number used when creating
                this shipment.
              </p>
            </div>
          </div>
        </form>
      </CardContent>
      <CardFooter>
        <Button
          type="submit"
          className="w-full"
          disabled={authMutation.isPending || phoneLastFour.length !== 4}
          onClick={handleSubmit}
        >
          {authMutation.isPending ? (
            <>Loading...</>
          ) : (
            <>
              <Unlock className="mr-2 h-4 w-4" />
              Verify Identity
            </>
          )}
        </Button>
      </CardFooter>
    </Card>
  );
};
